import { FC, useMemo, useRef } from "react";
import styles from "./ResultList.module.css";
import { Col, Row, message } from "antd";

import {
  IHackathonResponse,
  LIST_HACKATHON_PAGE_SIZE,
  adminHideHackathon,
  useInfinityListHackathon,
} from "api/hackathon";
import { useAppSelector } from "hooks";
import { getFilterPayload } from "store/ducks/filterHackathon/slice";
import useIntersectionObserver from "hooks/useIntersectionObserver";
import { Hidden } from "components/Hidden";
import { DisabledCard } from "components/DisabledCard";
import { HackathonCard } from "components/Hackathon";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useUser } from "api/auth";
import { useMutation } from "react-query";
import { AxiosError } from "axios";
import { getLoginState } from "store/ducks/auth/slice";

interface Props {
  view: "list" | "grid";
}

const ResultList: FC<Props> = ({ view }) => {
  const { lg } = useBreakpoint();
  const filterParams = useAppSelector(getFilterPayload);
  const isLoggedIn = useAppSelector(getLoginState);
  const ref = useRef<any>(null);
  const prevFilter = useRef(filterParams);
  const { data: userData } = useUser({ enabled: isLoggedIn });

  const {
    data,
    isFetching,
    hasNextPage,
    fetchNextPage,
    refetch: refetchHackathon,
  } = useInfinityListHackathon(
    {
      ...filterParams,
      ...(userData?.role === "Admin" ? {} : { isHide: false }),
    },
    {
      enabled: !isLoggedIn || Boolean(userData),
      getNextPageParam: (page) => {
        if (
          JSON.stringify(prevFilter.current) !== JSON.stringify(filterParams)
        ) {
          prevFilter.current = filterParams;
          return 0;
        }
        const pagedResult = page?.pagedResult;
        const currentPage = Number(pagedResult.page);
        const totalItems = Number(pagedResult.totalItems);
        const totalPage = Math.ceil(
          pagedResult.totalItems / LIST_HACKATHON_PAGE_SIZE
        );
        const isLastPage = currentPage === totalPage;
        if (totalItems === 0) return undefined;
        return isLastPage ? undefined : currentPage + 1;
      },
    }
  );

  const { mutate: handleAdminHideToggleHackathon } = useMutation(
    (variable: string) => adminHideHackathon({ hktId: variable }),
    {
      onSuccess: (data) => {
        refetchHackathon();
      },
      onError: (err: AxiosError) => {
        message.error(err.response?.data.message);
      },
    }
  );

  useIntersectionObserver({
    target: ref,
    onIntersect: fetchNextPage,
    enabled: hasNextPage,
  });

  const lastItem = useMemo(() => {
    const lastItemArray = data?.pages[data.pages.length - 1];
    const lastItemLength = lastItemArray?.pagedResult?.items?.length || 1;
    return lastItemArray?.pagedResult?.items[
      lastItemLength - 1
    ] as IHackathonResponse;
  }, [data?.pages]);

  const total = useMemo(() => {
    return data?.pages[data.pages.length - 1].pagedResult.totalItems || 0;
  }, [data?.pages]);

  const grid = useMemo(() => {
    if (!lg) return "grid";
    return view;
  }, [view, lg]);

  return (
    <div className={styles.root}>
      {/* <Hidden cond={!isFetching}>
        <div className={styles.resultStatistic}>
          {total} Total hackathon results found
        </div>
      </Hidden> */}

      <Row gutter={[15, 24]}>
        {data?.pages?.map((page) =>
          page.pagedResult?.items?.map((item) => (
            <Col
              key={item._id}
              xs={24}
              md={grid === "grid" ? 12 : 24}
              lg={grid === "grid" ? 8 : 24}
            >
              <HackathonCard
                variant={grid === "grid" ? "vertical" : "horizontal"}
                data={item}
                allowToHide={userData?.role === "Admin"}
                onHideToggle={() => handleAdminHideToggleHackathon(item._id)}
                isHidden={item.is_hide}
              />
            </Col>
          ))
        )}
      </Row>

      <Hidden js cond={!isFetching && hasNextPage && !!lastItem}>
        <DisabledCard onClick={fetchNextPage}>
          <HackathonCard
            variant={grid === "grid" ? "vertical" : "horizontal"}
            data={lastItem}
          />
        </DisabledCard>
      </Hidden>

      <Hidden cond={!hasNextPage && !isFetching && total !== 0}>
        <div className="center text-18 mt-24 rubik">
          Congrats! You have scrolled through all the hackathons.
        </div>
      </Hidden>

      <Hidden js cond={hasNextPage}>
        <div ref={ref}>&emsp;</div>
      </Hidden>
    </div>
  );
};

export default ResultList;
